import React, { useState } from "react";
import { Car, ArrowRight, Share2 } from "lucide-react";
import { Button } from "ui/button";

type LegType = "ARRIVAL" | "DEPARTURE" | "INTER-HOTEL" | "SIGHTSEEING";

  interface TourSharing {
    count: number;
    names: string[];
  }

  interface Leg {
    id: string;
    time: string;
    from: string;
    to: string;
    type: LegType;
    seatsUsed: number;
    seatsTotal: number;
    fillPct: number;
    sharing?: TourSharing;
  }

  interface Vehicle {
    id: string;
    name: string;
    vehicleType: string;
    capacity: number;
    legs: Leg[];
    avgUtilization: number;
  }

  const LEG_TYPE_STYLES: Record<
    LegType,
    { dot: string; label: string; bg: string; text: string }
  > = {
    ARRIVAL: {
      dot: "bg-emerald-500",
      label: "ARRIVAL",
      bg: "bg-emerald-50",
      text: "text-emerald-700",
    },
    DEPARTURE: {
      dot: "bg-amber-500",
      label: "DEPARTURE",
      bg: "bg-amber-50",
      text: "text-amber-700",
    },
    "INTER-HOTEL": {
      dot: "bg-blue-400",
      label: "INTER-HOTEL",
      bg: "bg-blue-50",
      text: "text-blue-600",
    },
    SIGHTSEEING: {
      dot: "bg-blue-400",
      label: "SIGHTSEEING",
      bg: "bg-blue-50",
      text: "text-blue-700",
    },
  };

  const VEHICLES: Vehicle[] = [
    {
      id: "v1",
      name: "KRB-2048",
      vehicleType: "Coach",
      capacity: 3,
      avgUtilization: 67,
      legs: [
        {
          id: "l1",
          time: "19:15",
          from: "Airport T2",
          to: "Najaf",
          type: "ARRIVAL",
          seatsUsed: 1,
          seatsTotal: 3,
          fillPct: 67,
        },
      ],
    },
    {
      id: "v2",
      name: "BGD-778",
      vehicleType: "Van",
      capacity: 6,
      avgUtilization: 83,
      legs: [
        {
          id: "l2",
          time: "06:20",
          from: "Airport T2",
          to: "Najaf",
          type: "ARRIVAL",
          seatsUsed: 5,
          seatsTotal: 6,
          fillPct: 83,
          sharing: { count: 2, names: ["Faiz Booking", "Instant Tour Booking"] },
        },
        {
          id: "l3",
          time: "11:50",
          from: "Airport T1",
          to: "Najaf",
          type: "ARRIVAL",
          seatsUsed: 6,
          seatsTotal: 6,
          fillPct: 100,
        },
        {
          id: "l4",
          time: "15:30",
          from: "Airport T1",
          to: "Najaf",
          type: "INTER-HOTEL",
          seatsUsed: 4,
          seatsTotal: 6,
          fillPct: 67,
        },
      ],
    },
    {
      id: "v3",
      name: "EV-8831",
      vehicleType: "Minibus",
      capacity: 13,
      avgUtilization: 44,
      legs: [
        {
          id: "l5",
          time: "08:00",
          from: "Airport T1",
          to: "Karbala",
          type: "ARRIVAL",
          seatsUsed: 6,
          seatsTotal: 13,
          fillPct: 46,
        },
        {
          id: "l6",
          time: "14:00",
          from: "Karbala",
          to: "Karbala",
          type: "SIGHTSEEING",
          seatsUsed: 5,
          seatsTotal: 13,
          fillPct: 78,
        },
        {
          id: "l7",
          time: "18:45",
          from: "Karbala",
          to: "Airport T2",
          type: "DEPARTURE",
          seatsUsed: 7,
          seatsTotal: 13,
          fillPct: 54,
        },
        {
          id: "l8",
          time: "22:10",
          from: "Karbala",
          to: "Airport T1",
          type: "DEPARTURE",
          seatsUsed: 5,
          seatsTotal: 13,
          fillPct: 38,
          sharing: { count: 1, names: ["Faiz Tour"] },
        },
      ],
    },
    {
      id: "v4",
      name: "NJF-0785",
      vehicleType: "Coach",
      capacity: 45,
      avgUtilization: 91,
      legs: [
        {
          id: "l9",
          time: "07:30",
          from: "Airport T1",
          to: "Najaf",
          type: "ARRIVAL",
          seatsUsed: 41,
          seatsTotal: 45,
          fillPct: 91,
          sharing: {
            count: 3,
            names: ["Instant Tour", "Faiz Tour", "External Tour"],
          },
        },
        {
          id: "l10",
          time: "20:00",
          from: "Najaf",
          to: "Airport T1",
          type: "DEPARTURE",
          seatsUsed: 40,
          seatsTotal: 45,
          fillPct: 89,
        },
      ],
    },
  ];

  function ProgressBar({ pct }: { pct: number }) {
    return (
      <div className="mt-2 h-1 w-full rounded-full bg-gray-100">
        <div
          className="h-full rounded-full bg-emerald-500 transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
    );
  }

  function LegTypeBadge({ type }: { type: LegType }) {
    const s = LEG_TYPE_STYLES[type];
    return (
      <span
        className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${s.bg} ${s.text} tracking-wide`}
      >
        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${s.dot}`} />
        {s.label}
      </span>
    );
  }

  function Card({ leg }: { leg: Leg }) {
    const [showSharing, setShowSharing] = useState(false);

    return (
      <div className="relative w-full min-w-0 rounded-xl border border-gray-200 bg-white p-3 shadow-md">
        <p className="mb-1 font-mono text-base font-bold text-amber-500">
          {leg.time}
        </p>
        <div className="mb-2 flex min-w-0 items-center justify-between gap-2">
          <p className="min-w-0 truncate text-sm font-semibold leading-snug text-gray-800">
            {leg.from} → {leg.to}
          </p>
          {leg.sharing && (
            <button
              type="button"
              className="flex-shrink-0 border-0 bg-transparent p-0"
              onMouseEnter={() => setShowSharing(true)}
              onMouseLeave={() => setShowSharing(false)}
              aria-label="View tour sharing details"
            >
              <Share2 className="h-4 w-4 text-violet-600" />
            </button>
          )}
        </div>

        <div className="flex min-w-0 items-center gap-2">
          <LegTypeBadge type={leg.type} />
          <span className="truncate text-xs text-gray-500">
            {leg.seatsUsed}/{leg.seatsTotal} seats · {leg.fillPct}% full
          </span>
        </div>
        <ProgressBar pct={leg.fillPct} />
        {leg.sharing && showSharing && (
          <div className="pointer-events-none absolute left-0 z-[100] w-full top-0 -translate-y-1/2 rounded-xl bg-gray-900 px-1 py-1 text-center text-[11px] font-medium leading-relaxed text-white shadow-lg whitespace-normal break-words">
            Shared by {leg.sharing.count}{" "}
            {leg.sharing.count === 1 ? "tour" : "tours"}:{" "}
            {leg.sharing.names.join(", ")}
          </div>
        )}
      </div>
    );
  }

  function VehicleCard({ vehicle }: { vehicle: Vehicle }) {
    return (
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-visible">
        {/* Green left accent bar */}
        <div className="flex">
          <div className="w-1 bg-emerald-400 flex-shrink-0 rounded-l-xl" />
          <div className="flex-1 p-4">
            {/* Vehicle header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
                  <Car className="w-5 h-5 text-emerald-500" />
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-800">
                    {vehicle.name}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {vehicle.vehicleType} · {vehicle.capacity} pax capacity
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 items-center gap-x-8 gap-y-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
              {vehicle.legs.map((leg, idx) => (
                <div key={leg.id} className="relative flex min-w-0 items-center">
                  <Card leg={leg} />

                  {idx < vehicle.legs.length - 1 && (
                    <div className="pointer-events-none absolute -right-7 top-1/2 z-10 flex -translate-y-1/2 items-center justify-center text-violet-600">
                      <ArrowRight className="h-5 w-5 stroke-[2.5]" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  export function Schedule() {
    return (
      <div className="space-y-4">
        <div className="flex justify-end">
          <Button className="h-8 bg-sky-600 px-3 text-xs text-white hover:bg-sky-700">
            Recommendation Schedule
          </Button>
        </div>

        <div className="space-y-4">
          {VEHICLES.map((v) => (
            <VehicleCard key={v.id} vehicle={v} />
          ))}
        </div>
      </div>
    );
  }

  export default Schedule;
